// The SCHEDULER CALENDAR (Scheduler Screen v2, the "Signal" re-layout): a month of day cells, each carrying
// the runs that BELONG to that day — a past day shows the RECORDED Run Records that started then
// (failed/interrupted visibly distinct from completed, G-107/G-111), a future day shows the SERVER-projected
// occurrences (the scheduler's deterministic, bounded date utility — G-110; the client only PLACES them, it
// never computes a recurrence). Beside it: the selected day's agenda and the honest at-a-glance card
// (recent-window tallies + paused/draft counts). Colour carries genuine status only — attention is the one
// toned chip. Tokens only (G-181); no internal id renders (G-197).
//
// The month is a WINDOW the human moves (Previous/Next). Moving it re-lays the same placed entries; it
// fetches nothing and projects nothing, so a month the projection is silent about shows empty days and
// says so — never a padded month of plausible-looking runs.

import type { ScheduleHealthScheduleRow } from '../projections.ts';
import { Icon } from '../../app/Icon.tsx';
import { entriesByDay, type DayEntry } from './routine-day-entries.ts';
import { localIsoDate } from './scheduler-format.ts';
import { RoutineMonthCard } from './RoutineMonthCard.tsx';

/** One agenda entry — a button, because clicking it opens that routine's detail panel. */
function AgendaRow({
  entry,
  onOpen,
}: {
  readonly entry: DayEntry;
  readonly onOpen: (scheduleId: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="ra-row"
      data-tone={entry.tone}
      aria-label={`Open routine: ${entry.name}`}
      onClick={() => onOpen(entry.scheduleId)}
    >
      <span className="tm">{entry.time}</span>
      <span className="bar" aria-hidden="true" />
      <span className="body">
        <span className="nm">{entry.name}</span>
        <span className="st">{entry.stateText}</span>
      </span>
    </button>
  );
}

/** The selected day's agenda: every placed entry with its honest state line, each opening its routine. */
function DayAgenda({
  day,
  entries,
  todayIso,
  onOpen,
}: {
  readonly day: string;
  readonly entries: readonly DayEntry[];
  readonly todayIso: string;
  readonly onOpen: (scheduleId: string) => void;
}): React.JSX.Element {
  const parsed = new Date(`${day}T00:00:00`);
  const title =
    day === todayIso
      ? `Today, ${parsed.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric' })}`
      : parsed.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
  return (
    <div className="rs-card" data-agenda-day={day}>
      <div className="rs-head">
        <h2 className="h-section">{title}</h2>
        <span className="mono-label">
          {entries.length === 0
            ? 'nothing listed'
            : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
        </span>
      </div>
      {entries.length === 0 ? (
        <div className="rs-empty">
          Nothing is projected or recorded for this day. Pick another, or add a routine and Ritona
          will find it a slot.
        </div>
      ) : (
        entries.map((entry) => (
          <AgendaRow key={entry.instant + entry.name} entry={entry} onOpen={onOpen} />
        ))
      )}
    </div>
  );
}

/** The honest at-a-glance card: recent-window run tallies + the paused/draft shelf with Show links. */
function GlanceCard({
  schedules,
  onShowPaused,
  onShowDrafts,
}: {
  readonly schedules: readonly ScheduleHealthScheduleRow[];
  readonly onShowPaused: () => void;
  readonly onShowDrafts: () => void;
}): React.JSX.Element {
  const landed = schedules.reduce((total, row) => total + row.outcomeTally.completed, 0);
  const failed = schedules.reduce(
    (total, row) => total + row.recentFailureCount + row.recentInterruptedCount,
    0,
  );
  const paused = schedules.filter((row) => row.state === 'paused').length;
  const drafts = schedules.filter((row) => row.state === 'draft').length;
  return (
    <div className="rs-card">
      <h2 className="h-section">At a glance</h2>
      <div className="rg-row">
        <span className="ic ok" aria-hidden="true">
          <Icon name={failed === 0 ? 'check' : 'pulse'} size={15} />
        </span>
        <span className="tx">
          {failed === 0
            ? `${landed} recent runs, all landed`
            : `${failed} recent ${failed === 1 ? 'run' : 'runs'} failed or interrupted`}
        </span>
      </div>
      {paused > 0 ? (
        <GlanceShelfRow
          pending
          text={`${paused} paused ${paused === 1 ? 'routine' : 'routines'}`}
          showLabel="Show paused routines"
          onShow={onShowPaused}
        />
      ) : null}
      {drafts > 0 ? (
        <GlanceShelfRow
          text={`${drafts} inactive ${drafts === 1 ? 'draft' : 'drafts'}`}
          showLabel="Show draft routines"
          onShow={onShowDrafts}
        />
      ) : null}
    </div>
  );
}

/** One quiet glance row with its Show jump (paused / drafts). */
function GlanceShelfRow({
  text,
  showLabel,
  onShow,
  pending = false,
}: {
  readonly text: string;
  readonly showLabel: string;
  readonly onShow: () => void;
  readonly pending?: boolean;
}): React.JSX.Element {
  return (
    <div className="rg-row">
      <span className={pending ? 'dot pending' : 'dot'} aria-hidden="true" />
      <span className="tx">{text}</span>
      <button type="button" className="rg-show" onClick={onShow} aria-label={showLabel}>
        Show
      </button>
    </div>
  );
}

/** What the calendar view renders. `monthAnchor` is any date inside the month being shown. */
export interface RoutineCalendarProps {
  readonly schedules: readonly ScheduleHealthScheduleRow[];
  readonly monthAnchor: Date;
  readonly onStepMonth: (step: number) => void;
  readonly selectedDay: string;
  readonly onSelectDay: (day: string) => void;
  readonly onOpenRoutine: (scheduleId: string) => void;
  readonly onShowPaused: () => void;
  readonly onShowDrafts: () => void;
}

/** The calendar view: the month card + the selected-day agenda + the at-a-glance card. */
export function RoutineCalendar({
  schedules,
  monthAnchor,
  onStepMonth,
  selectedDay,
  onSelectDay,
  onOpenRoutine,
  onShowPaused,
  onShowDrafts,
}: RoutineCalendarProps): React.JSX.Element {
  const todayIso = localIsoDate(new Date());
  const byDay = entriesByDay(schedules);
  return (
    <div className="rt-cal">
      <RoutineMonthCard
        anchor={monthAnchor}
        todayIso={todayIso}
        byDay={byDay}
        selectedDay={selectedDay}
        onSelectDay={onSelectDay}
        onStepMonth={onStepMonth}
      />
      <div className="rt-side">
        <DayAgenda
          day={selectedDay}
          entries={byDay.get(selectedDay) ?? []}
          todayIso={todayIso}
          onOpen={onOpenRoutine}
        />
        <GlanceCard schedules={schedules} onShowPaused={onShowPaused} onShowDrafts={onShowDrafts} />
      </div>
    </div>
  );
}
